/**
 * Safe localStorage helpers.
 * localStorage can throw (Safari private browsing, storage disabled) and
 * stored values can be missing or corrupted, so every read is validated
 * and every write is guarded.
 */

/**
 * Read an integer setting, clamped to a valid range
 * @param {string} key - localStorage key
 * @param {{min: number, max: number, fallback: number}} options - Validation range and fallback
 * @returns {number} The validated value, or the fallback
 */
export function readInt(key, { min, max, fallback }) {
    let raw = null;
    try {
        raw = localStorage.getItem(key);
    } catch (e) {
        return fallback;
    }
    if (raw === null) return fallback;

    const value = parseInt(raw, 10);
    if (isNaN(value)) return fallback;

    return Math.min(max, Math.max(min, value));
}

/**
 * Read a string setting restricted to an allowlist
 * @param {string} key - localStorage key
 * @param {string[]} allowedValues - Values considered valid
 * @param {string} fallback - Value to use when missing or invalid
 * @returns {string} The stored value if allowed, otherwise the fallback
 */
export function readString(key, allowedValues, fallback) {
    try {
        const raw = localStorage.getItem(key);
        return allowedValues.includes(raw) ? raw : fallback;
    } catch (e) {
        return fallback;
    }
}

/**
 * Write a setting, ignoring storage failures
 * @param {string} key - localStorage key
 * @param {string|number} value - Value to store
 */
export function write(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        // Storage unavailable (private browsing, quota) - the setting just won't persist
    }
}

/**
 * Read and parse a JSON value
 * @param {string} key - localStorage key
 * @returns {*} The parsed value, or null when missing or corrupted
 */
export function readJSON(key) {
    try {
        const raw = localStorage.getItem(key);
        return raw === null ? null : JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

/**
 * Serialize and store a JSON value, ignoring storage failures
 * @param {string} key - localStorage key
 * @param {*} value - JSON-serializable value
 */
export function writeJSON(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        // Storage unavailable - progress just won't persist
    }
}

/**
 * Remove a stored key, ignoring storage failures
 * @param {string} key - localStorage key
 */
export function remove(key) {
    try {
        localStorage.removeItem(key);
    } catch (e) {
        // Storage unavailable
    }
}
